import React from "react"

import { Tooltip } from "@chakra-ui/core"
import { DocumentArrowDownIcon } from "@heroicons/react/24/solid"

import classNames from "src/classNames"
import dayjs from "src/dayjs"

import ExpandableImageCollection from "components/ExpandableImageCollection"
import LocalTime from "components/LocalTime"
import Avatar from "components/Messaging/Avatar"

import ReportMessageButton from "./ReportModal"

export type MessageProps = {
  viewer: {
    id: string
    type: "Teacher" | "Tutor" | "Student" | "Parent"
  }
  message: {
    id: string
    read?: boolean
    content: string
    createdAt: string
    contentFlagged: boolean
    author: {
      id: string
      type: "Teacher" | "Student" | "Tutor" | "Parent"
      messagingDisplayName: string
      firstName: string
      avatar: {
        thumbUrl: string
      }
    }
    attachment?: {
      filename: string
      downloadUrl: string
      url: string
      thumbUrl: string
      previewUrl: string
      contentType: string
    }
  }
}

const Message: React.FC<MessageProps> = ({ message, viewer }) => {
  const {
    id,
    content,
    attachment,
    author,
    createdAt,
    contentFlagged,
    read = true,
  } = message

  const authorView = author.id === viewer.id

  return (
    <div
      className={classNames("mb-2 flex w-full", authorView && "justify-end")}
    >
      {!authorView && (
        <div className="mr-2 hidden shrink-0 md:block">
          <Avatar
            src={author.avatar.thumbUrl}
            alt={author.messagingDisplayName}
          />
        </div>
      )}
      <div
        className={classNames(
          "flex w-full flex-col",
          authorView ? "items-end" : "items-start"
        )}
      >
        <div
          className={classNames(
            "flex",
            authorView ? "justify-end" : "justify-start"
          )}
        >
          <div
            className={classNames(
              "min-w-[14rem] max-w-[85%] rounded-2xl px-3 py-2 shadow-md",
              authorView
                ? "rounded-tr-none bg-sky-200 text-gray-900"
                : "rounded-tl-none bg-gray-200 text-gray-700",
              !authorView && "w-full"
            )}
          >
            <div className="space-between mb-2 flex w-full items-center">
              <div className="flex grow flex-col">
                <h4 className="text-lg font-bold">
                  {authorView ? "You" : author.firstName}
                </h4>
              </div>
              {!authorView && !contentFlagged && (
                <ReportMessageButton messageId={id} />
              )}
            </div>

            <p className={classNames("mt-1", !read && "font-bold")}>
              {content}
            </p>
            {attachment?.downloadUrl && (
              <>
                {attachment.contentType.includes("image/") && (
                  <ExpandableImageCollection images={[attachment]} />
                )}
                <div className="my-2">
                  <a
                    href={attachment.downloadUrl}
                    className="flex items-center font-medium text-sky-700"
                  >
                    <DocumentArrowDownIcon className="mr-2 h-5 w-5" />
                    <span>Download {attachment.filename}</span>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
        <div
          className={classNames(
            "mt-0.5 w-full",
            authorView ? "text-right" : "text-left"
          )}
        >
          <Tooltip
            label={dayjs(createdAt).format("MMM D, YYYY h:mma")}
            placement="top"
            aria-label={`Time sent: ${dayjs(createdAt).format(
              "MMM D, YYYY h:mma"
            )}`}
            className="rounded-md bg-gray-600 py-1 px-2 text-white"
          >
            <span className="cursor-default text-xs text-slate-500">
              <LocalTime timestamp={createdAt} relative />
            </span>
          </Tooltip>
        </div>
      </div>
      {authorView && (
        <div className="ml-2 hidden shrink-0 md:block">
          <Avatar
            src={author.avatar.thumbUrl}
            alt={author.messagingDisplayName}
          />
        </div>
      )}
    </div>
  )
}

export default Message
